import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Plus,
  RefreshCw,
  Trash2,
  Webhook,
  X,
} from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import {
  useCreateWebhookEndpoint,
  useCurrentUser,
  useDeleteWebhookEndpoint,
  useRetryWebhookDelivery,
  useRotateWebhookSecret,
  useUpdateWebhookEndpoint,
  useWebhookDeliveries,
  useWebhookEndpoints,
} from "@/api/hooks";
import type { WebhookDeliveryResponse, WebhookEndpointResponse } from "@/api/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";

/** Outbound webhook subscription manager.
 *
 *  Org admins (and PencilOut staff) wire PencilOut events into
 *  Zapier / Make / n8n / their own backend. Each endpoint has a
 *  signing secret used to HMAC-SHA256 sign outbound POSTs; the
 *  recipient verifies signatures with that same secret.
 *
 *  This page surfaces:
 *  - List of endpoints (left) with status indicators.
 *  - Editor (right) for the selected endpoint: URL, description,
 *    event-type allowlist, enabled toggle, rotate-secret, delete.
 *  - Recent delivery audit (bottom of right pane) showing the
 *    last N attempts per endpoint with status code + body excerpt
 *    so contractors can self-debug "why isn't my Zapier firing?".
 *
 *  Rotated/created secrets surface in a one-time-reveal banner with
 *  a copy button. The plaintext is NEVER returned again on
 *  subsequent reads — same Stripe convention, minimizes leak risk.
 */
export function WebhookEndpointsPage() {
  const me = useCurrentUser();
  const myRole = me.data?.role;
  const canManage = myRole === "pencilout_admin" || myRole === "org_admin";

  const list = useWebhookEndpoints();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /// One-shot reveal panel — shows the freshly-minted secret. Cleared
  /// after the user clicks "I've copied it."
  const [revealedSecret, setRevealedSecret] = useState<{
    endpointId: string;
    secret: string;
    note: string;
  } | null>(null);

  // Auto-select on data refresh — same UX as JobTemplatesPage.
  useEffect(() => {
    if (!list.data) return;
    if (selectedId && !list.data.find((e) => e.id === selectedId)) {
      setSelectedId(list.data[0]?.id ?? null);
    } else if (!selectedId && list.data.length > 0) {
      const first = list.data[0];
      if (first) setSelectedId(first.id);
    }
  }, [list.data, selectedId]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-mono text-2xl font-bold tracking-tight">Webhooks</h1>
          <p className="mt-1 max-w-3xl text-sm text-graphite-soft">
            Pipe PencilOut events into Zapier, Make, n8n, or your own backend. Each event POSTs JSON
            to your endpoint with an HMAC-SHA256 signature you can verify using the per-endpoint
            secret. Failed deliveries persist for inspection below.
          </p>
        </div>
        {canManage && !creating && (
          <button
            type="button"
            onClick={() => {
              setCreating(true);
              setSelectedId(null);
            }}
            className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden /> New endpoint
          </button>
        )}
      </div>

      {revealedSecret && (
        <SecretRevealBanner
          secret={revealedSecret.secret}
          note={revealedSecret.note}
          onDismiss={() => setRevealedSecret(null)}
        />
      )}

      {list.isLoading && (
        <div className="rounded-lg border border-rule bg-paper-warm p-6 font-mono text-sm text-graphite-soft">
          Loading…
        </div>
      )}
      {list.isError && (
        <div className="rounded-lg border border-red-pencil/30 bg-red-pencil/5 p-4 font-mono text-sm text-red-pencil">
          {list.error?.message ?? "Failed to load endpoints."}
        </div>
      )}

      {creating && canManage && (
        <CreateEndpointForm
          onClose={(saved) => {
            setCreating(false);
            if (saved) {
              setSelectedId(saved.endpointId);
              setRevealedSecret(saved);
            }
          }}
        />
      )}

      {list.data && list.data.length === 0 && !creating && (
        <div className="rounded-lg border border-dashed border-rule bg-paper-warm p-10 text-center">
          <Webhook className="mx-auto h-8 w-8 text-graphite-faint" aria-hidden />
          <p className="mt-3 font-mono text-sm font-semibold text-graphite">No webhooks yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-graphite-soft">
            Hit "New endpoint" to subscribe an external URL. Each endpoint receives signed POSTs
            whenever the events you care about fire.
          </p>
        </div>
      )}

      {list.data && list.data.length > 0 && !creating && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
          <EndpointList endpoints={list.data} selectedId={selectedId} onSelect={setSelectedId} />
          {(() => {
            const selected = selectedId ? list.data.find((e) => e.id === selectedId) : undefined;
            if (!selected || !selectedId) return null;
            return (
              <EndpointEditor
                key={selectedId}
                endpoint={selected}
                canManage={canManage}
                onSecretRotated={(secret) =>
                  setRevealedSecret({
                    endpointId: selectedId,
                    secret,
                    note: "Secret rotated. The previous secret is invalid as of now.",
                  })
                }
                onDeleted={() => setSelectedId(null)}
              />
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------

/** One-shot panel that surfaces a freshly-minted secret with a
 *  copy button. The user has to acknowledge the panel to dismiss
 *  it — otherwise we'd risk them losing the only chance to see
 *  the plaintext. */
function SecretRevealBanner({
  secret,
  note,
  onDismiss,
}: {
  secret: string;
  note: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-lg border-2 border-pencil-yellow bg-pencil-yellow-soft p-4">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-graphite" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="font-mono text-sm font-semibold text-graphite">Save this secret now</p>
          <p className="mt-0.5 text-xs text-graphite-soft">
            {note} It won't be shown again — copy it into your recipient's setup before dismissing.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 select-all overflow-x-auto rounded border border-rule bg-paper px-3 py-1.5 font-mono text-xs">
              {secret}
            </code>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(secret).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                });
              }}
              className="inline-flex items-center gap-1 rounded border border-rule bg-paper px-2 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="h-3 w-3" aria-hidden /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3" aria-hidden /> Copy
                </>
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="mt-3 font-mono text-xs text-graphite-soft hover:text-graphite"
          >
            I've saved it · dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

function EndpointList({
  endpoints,
  selectedId,
  onSelect,
}: {
  endpoints: WebhookEndpointResponse[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <ul className="divide-y divide-rule overflow-hidden rounded-lg border border-rule bg-paper-warm">
      {endpoints.map((e) => {
        const isActive = e.id === selectedId;
        return (
          <li key={e.id}>
            <button
              type="button"
              onClick={() => onSelect(e.id)}
              className={`flex w-full items-start gap-3 p-3 text-left transition hover:bg-paper ${
                isActive ? "bg-paper" : ""
              }`}
            >
              <span
                className={`mt-1 inline-block h-2 w-2 flex-none rounded-full ${
                  e.enabled ? "bg-graphite" : "bg-graphite-faint"
                }`}
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-mono text-sm font-semibold text-graphite">
                  {e.description || hostnameOf(e.url)}
                </span>
                <span className="mt-0.5 block truncate font-mono text-[11px] text-graphite-soft">
                  {e.url}
                </span>
                <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-wider text-graphite-faint">
                  {e.event_types.length === 0
                    ? "All events"
                    : `${e.event_types.length} event${e.event_types.length === 1 ? "" : "s"}`}
                  {!e.enabled && " · disabled"}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function EndpointEditor({
  endpoint,
  canManage,
  onSecretRotated,
  onDeleted,
}: {
  endpoint: WebhookEndpointResponse;
  canManage: boolean;
  onSecretRotated: (secret: string) => void;
  onDeleted: () => void;
}) {
  const [url, setUrl] = useState(endpoint.url);
  const [description, setDescription] = useState(endpoint.description ?? "");
  const [eventTypes, setEventTypes] = useState(endpoint.event_types.join(", "));
  const [enabled, setEnabled] = useState(endpoint.enabled);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Re-sync local state when the parent prop changes (e.g. after
  // a sibling action refreshed the cache).
  useEffect(() => {
    setUrl(endpoint.url);
    setDescription(endpoint.description ?? "");
    setEventTypes(endpoint.event_types.join(", "));
    setEnabled(endpoint.enabled);
  }, [endpoint.url, endpoint.description, endpoint.event_types, endpoint.enabled]);

  const update = useUpdateWebhookEndpoint(endpoint.id);
  const del = useDeleteWebhookEndpoint();
  const rotate = useRotateWebhookSecret();

  const dirty =
    url !== endpoint.url ||
    description !== (endpoint.description ?? "") ||
    eventTypes !== endpoint.event_types.join(", ") ||
    enabled !== endpoint.enabled;

  function handleSave() {
    setErrorMsg(null);
    const cleanedTypes = eventTypes
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    update.mutate(
      {
        url: url.trim() || undefined,
        description: description.trim() ? description.trim() : null,
        event_types: cleanedTypes,
        enabled,
      },
      {
        onSuccess: () => {
          setSavedFlash(true);
          setTimeout(() => setSavedFlash(false), 2000);
        },
        onError: (err) => setErrorMsg(err.message),
      },
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-rule bg-paper-warm p-5">
        <div className="space-y-4">
          <div>
            <label
              htmlFor={`endpoint-url-${endpoint.id}`}
              className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
            >
              Target URL
            </label>
            <input
              id={`endpoint-url-${endpoint.id}`}
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={!canManage}
              placeholder="https://hooks.zapier.com/…"
              className={fieldClass}
            />
          </div>

          <div>
            <label
              htmlFor={`endpoint-desc-${endpoint.id}`}
              className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
            >
              Label
            </label>
            <input
              id={`endpoint-desc-${endpoint.id}`}
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canManage}
              maxLength={255}
              placeholder="Zapier — quote signed → QBO"
              className={fieldClass}
            />
          </div>

          <div>
            <label
              htmlFor={`endpoint-events-${endpoint.id}`}
              className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
            >
              Event types
            </label>
            <input
              id={`endpoint-events-${endpoint.id}`}
              type="text"
              value={eventTypes}
              onChange={(e) => setEventTypes(e.target.value)}
              disabled={!canManage}
              placeholder="quote.signed, quote.declined"
              className={fieldClass}
            />
            <p className="mt-1 font-mono text-[11px] text-graphite-faint">
              Comma-separated. Leave blank to subscribe to all events.
            </p>
          </div>

          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={!canManage}
              className="mt-1"
            />
            <span className="text-sm">
              Enabled
              <span className="ml-2 font-mono text-[11px] text-graphite-soft">
                When off, events skip this endpoint without deleting it.
              </span>
            </span>
          </label>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            {canManage && (
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || update.isPending}
                className="rounded bg-graphite px-4 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
              >
                {update.isPending ? "Saving…" : "Save"}
              </button>
            )}
            {savedFlash && (
              <span className="inline-flex items-center gap-1 font-mono text-xs text-graphite-soft">
                <CheckCircle2 className="h-3.5 w-3.5 text-graphite" aria-hidden />
                Saved
              </span>
            )}
            <span className="ml-auto" />
            {canManage && (
              <button
                type="button"
                onClick={() => setConfirmRotate(true)}
                className="inline-flex items-center gap-1 rounded border border-rule bg-paper px-2 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow-soft"
              >
                <RefreshCw className="h-3 w-3" aria-hidden /> Rotate secret
              </button>
            )}
            {canManage && (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-1 rounded border border-red-pencil/30 bg-red-pencil/5 px-2 py-1.5 font-mono text-xs font-semibold text-red-pencil transition hover:bg-red-pencil/10"
              >
                <Trash2 className="h-3 w-3" aria-hidden /> Delete
              </button>
            )}
            {errorMsg && (
              <span className="basis-full font-mono text-xs text-red-pencil">{errorMsg}</span>
            )}
          </div>
        </div>
      </div>

      <DeliveriesPanel endpointId={endpoint.id} />

      {confirmDelete && (
        <ConfirmDialog
          title="Delete this webhook?"
          body="The endpoint and all its delivery history will be removed. Events that fire after this won't reach this URL."
          confirmLabel="Delete"
          variant="danger"
          busy={del.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            del.mutate(endpoint.id, {
              onSuccess: () => {
                setConfirmDelete(false);
                onDeleted();
              },
              onError: (err) => {
                setErrorMsg(err.message);
                setConfirmDelete(false);
              },
            });
          }}
        />
      )}

      {confirmRotate && (
        <ConfirmDialog
          title="Rotate signing secret?"
          body={
            "The current secret will stop working immediately. Make sure your downstream system has the new value before your next event fires."
          }
          confirmLabel="Rotate"
          busy={rotate.isPending}
          onCancel={() => setConfirmRotate(false)}
          onConfirm={() => {
            rotate.mutate(endpoint.id, {
              onSuccess: (resp) => {
                setConfirmRotate(false);
                onSecretRotated(resp.signing_secret);
              },
              onError: (err) => {
                setErrorMsg(err.message);
                setConfirmRotate(false);
              },
            });
          }}
        />
      )}
    </div>
  );
}

function CreateEndpointForm({
  onClose,
}: {
  onClose: (saved?: { endpointId: string; secret: string; note: string }) => void;
}) {
  const create = useCreateWebhookEndpoint();
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [eventTypes, setEventTypes] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    if (!url.trim()) {
      setErrorMsg("URL is required.");
      return;
    }
    const cleanedTypes = eventTypes
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    create.mutate(
      {
        url: url.trim(),
        description: description.trim() || null,
        event_types: cleanedTypes.length ? cleanedTypes : null,
      },
      {
        onSuccess: (saved) => {
          onClose({
            endpointId: saved.id,
            secret: saved.signing_secret,
            note: "Endpoint created. Use this secret to verify outbound POSTs.",
          });
        },
        onError: (err) => setErrorMsg(err.message),
      },
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 rounded-lg border border-rule bg-paper-warm p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
            New endpoint
          </h2>
          <p className="mt-1 text-sm text-graphite-soft">
            We'll mint a signing secret on save. You'll see it once — copy it into your recipient's
            setup before dismissing.
          </p>
        </div>
        <button
          type="button"
          onClick={() => onClose()}
          className="rounded border border-rule p-1.5 text-graphite-soft transition hover:bg-paper hover:text-graphite"
          aria-label="Cancel"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      <div>
        <label
          htmlFor="new-endpoint-url"
          className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
        >
          Target URL
        </label>
        <input
          id="new-endpoint-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          required
          placeholder="https://hooks.zapier.com/hooks/catch/…"
          className={fieldClass}
        />
      </div>

      <div>
        <label
          htmlFor="new-endpoint-desc"
          className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
        >
          Label
        </label>
        <input
          id="new-endpoint-desc"
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={255}
          placeholder="Zapier — quote signed → QBO"
          className={fieldClass}
        />
      </div>

      <div>
        <label
          htmlFor="new-endpoint-events"
          className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
        >
          Event types
        </label>
        <input
          id="new-endpoint-events"
          type="text"
          value={eventTypes}
          onChange={(e) => setEventTypes(e.target.value)}
          placeholder="quote.signed, walkthrough.processed"
          className={fieldClass}
        />
        <p className="mt-1 font-mono text-[11px] text-graphite-faint">
          Comma-separated. Leave blank to subscribe to all events. Currently supported:{" "}
          <code>quote.signed</code>.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded bg-graphite px-4 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
        >
          {create.isPending ? "Creating…" : "Create endpoint"}
        </button>
        <button
          type="button"
          onClick={() => onClose()}
          className="rounded border border-rule bg-paper px-4 py-2 font-mono text-sm font-semibold text-graphite transition hover:bg-paper-warm"
        >
          Cancel
        </button>
        {errorMsg && <span className="font-mono text-xs text-red-pencil">{errorMsg}</span>}
      </div>
    </form>
  );
}

function DeliveriesPanel({ endpointId }: { endpointId: string }) {
  const deliveries = useWebhookDeliveries(endpointId);
  const retry = useRetryWebhookDelivery(endpointId);
  return (
    <div className="rounded-lg border border-rule bg-paper-warm">
      <div className="flex items-center justify-between border-b border-rule px-5 py-3">
        <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
          Recent deliveries
        </h3>
        <span className="font-mono text-[11px] text-graphite-faint">
          Polling every 15s · last {deliveries.data?.length ?? 0}
        </span>
      </div>
      {deliveries.isLoading && (
        <p className="px-5 py-4 font-mono text-xs text-graphite-soft">Loading…</p>
      )}
      {deliveries.data && deliveries.data.length === 0 && (
        <p className="px-5 py-4 font-mono text-xs text-graphite-soft">
          No deliveries yet. Fire a matching event to test.
        </p>
      )}
      {deliveries.data && deliveries.data.length > 0 && (
        <ul className="divide-y divide-rule/60">
          {deliveries.data.map((d) => (
            <DeliveryRow
              key={d.id}
              delivery={d}
              onRetry={() => retry.mutate(d.id)}
              retrying={retry.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function DeliveryRow({
  delivery,
  onRetry,
  retrying,
}: {
  delivery: WebhookDeliveryResponse;
  onRetry: () => void;
  retrying: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ok =
    delivery.response_status !== null &&
    delivery.response_status >= 200 &&
    delivery.response_status < 300;
  const stamp = new Date(delivery.created_at).toLocaleString();
  // Retry is offered for any non-2xx outcome — including 4xx
  // rejections, since the recipient might have fixed the problem
  // (auth misconfig, bad signature secret reference) since the
  // original fire.
  const canRetry = !ok;
  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 px-5 py-2.5 text-left transition hover:bg-paper"
      >
        <span
          className={`mt-1 inline-block h-2 w-2 flex-none rounded-full ${
            ok ? "bg-green-600" : "bg-red-pencil"
          }`}
          aria-hidden
        />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-mono text-sm font-semibold">{delivery.event_type}</span>
            <span className="font-mono text-xs tabular-nums text-graphite-soft">{stamp}</span>
            <span className="font-mono text-xs text-graphite-soft">
              attempt {delivery.attempt} ·{" "}
              {delivery.response_status !== null
                ? `HTTP ${delivery.response_status}`
                : "transport error"}
            </span>
          </span>
          {delivery.error_message && !open && (
            <span className="mt-0.5 block truncate font-mono text-[11px] text-red-pencil">
              {delivery.error_message}
            </span>
          )}
        </span>
        <span
          className={`mt-1 font-mono text-[10px] text-graphite-faint transition ${open ? "rotate-90" : ""}`}
          aria-hidden
        >
          ▶
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-rule/40 bg-paper px-5 py-3 font-mono text-[11px] text-graphite-soft">
          {delivery.error_message && <p className="text-red-pencil">{delivery.error_message}</p>}
          {delivery.response_body_excerpt ? (
            <pre className="overflow-x-auto whitespace-pre-wrap">
              {delivery.response_body_excerpt}
            </pre>
          ) : (
            <p className="text-graphite-faint">(empty response body)</p>
          )}
          {canRetry && (
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry();
                }}
                disabled={retrying}
                className="inline-flex items-center gap-1 rounded border border-rule bg-paper-warm px-2 py-1 font-mono text-[11px] font-semibold text-graphite transition hover:bg-pencil-yellow-soft disabled:opacity-40"
              >
                <RefreshCw className={`h-3 w-3 ${retrying ? "animate-spin" : ""}`} aria-hidden />
                {retrying ? "Retrying…" : "Retry"}
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

const fieldClass =
  "mt-1 w-full rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none disabled:opacity-60";

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}
